import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@toki/auth';
import type { Area } from '../services/api';
import { listAreas, createArea, updateArea, deactivateArea } from '../services/api';
import styles from './Areas.module.css';

export function AreasScreen() {
  const { provider } = useAuth();
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Area | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setLoading(true);
    try {
      setAreas(await listAreas(token));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setContactEmail('');
    setContactPhone('');
    setShowForm(true);
    setError(null);
  };

  const openEdit = (area: Area) => {
    setEditing(area);
    setName(area.name);
    setDescription(area.description ?? '');
    setContactEmail(area.contactEmail ?? '');
    setContactPhone(area.contactPhone ?? '');
    setShowForm(true);
    setError(null);
  };

  const handleSave = async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      // Pydantic EmailStr rechaza string vacío; enviamos null cuando lo está.
      const payload = {
        name,
        description: description || null,
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
      };
      if (editing) {
        await updateArea(editing.id, payload, token);
      } else {
        await createArea(payload, token);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (area: Area) => {
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      await deactivateArea(area.id, token);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleReactivate = async (area: Area) => {
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      await updateArea(area.id, { isActive: true }, token);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className={styles.loading}>Cargando areas...</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Equipos</h2>
        <button className={styles.createBtn} onClick={openCreate}>+ Nuevo equipo</button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {showForm && (
        <div className={styles.form}>
          <h3>{editing ? 'Editar equipo' : 'Nuevo equipo'}</h3>
          <input
            className={styles.input}
            placeholder="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className={styles.input}
            placeholder="Descripcion (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <input
            className={styles.input}
            type="email"
            placeholder="Email de contacto del equipo"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
          <input
            className={styles.input}
            type="tel"
            placeholder="Telefono de contacto (opcional)"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
          />
          <div className={styles.formActions}>
            <button className={styles.saveBtn} onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button className={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}

      <div className={styles.tableCard}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Descripcion</th>
            <th>Contacto</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {areas.map((area) => (
            <tr key={area.id} className={!area.isActive ? styles.inactive : ''}>
              <td>{area.name}</td>
              <td>{area.description || '—'}</td>
              <td>
                {area.contactEmail || area.contactPhone ? (
                  <>
                    {area.contactEmail && <div>{area.contactEmail}</div>}
                    {area.contactPhone && <div>{area.contactPhone}</div>}
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td>
                <span className={area.isActive ? styles.badgeActive : styles.badgeInactive}>
                  {area.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </td>
              <td className={styles.actions}>
                <button className={styles.editBtn} onClick={() => openEdit(area)}>Editar</button>
                {area.isActive ? (
                  <button className={styles.deactivateBtn} onClick={() => handleDeactivate(area)}>Desactivar</button>
                ) : (
                  <button className={styles.editBtn} onClick={() => handleReactivate(area)}>Reactivar</button>
                )}
              </td>
            </tr>
          ))}
          {areas.length === 0 && (
            <tr><td colSpan={5} className={styles.empty}>No hay areas creadas</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
